import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  FilterList as FilterIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';

// Redux actions
import {
  getCustomFilters,
  createCustomFilter,
  updateCustomFilter,
  deleteCustomFilter,
  applyCustomFilters,
} from '../../../../redux/slices/operator';

// Trade schema
import { getFieldsByCategory } from './tradeSchema';
import { getFieldSchema } from './LineTradeFilterHelpers';

// Modular components
import SavedFiltersMenu from './SavedFiltersMenu';
import SaveFilterDialog from './SaveFilterDialog';
import AppliedFiltersDisplay from './AppliedFiltersDisplay';
import FilterConditionRow from './FilterConditionRow';

const FILTER_TYPES = {
  'line-trade': 'Line Trade',
  'trade': 'Trade',
  'portfolio': 'Portfolio',
  'dashboard': 'Dashboard',
  'user': 'User',
  'user-log': 'User Log',
  'message': 'Message',
  'weekly-profit': 'Weekly Profit',
  'user-balance': 'User Balance',
  'scripts': 'Scripts',
  'segments': 'Segments',
};

const LineTradeFilterBuilder = ({ filterType = 'line-trade', onApplyFilters, appliedFilters = [] }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { customFilters, isFilterLoading, filterError } = useSelector(
    (state) => state.operator
  );  // Handle both possible response formats: direct array or response with docs
  const actualCustomFilters = Array.isArray(customFilters)
    ? customFilters
    : (customFilters?.docs || []);

  // Local state for building filters
  const [conditions, setConditions] = useState([]);
  const [currentFilterName, setCurrentFilterName] = useState('');
  const [currentFilterDescription, setCurrentFilterDescription] = useState('');
  const [editingFilterId, setEditingFilterId] = useState(null);
  // Dialog states
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);

  // Get all available fields for dropdown
  const availableFields = getFieldsByCategory();

  useEffect(() => {
    dispatch(getCustomFilters(filterType));
  }, [dispatch, filterType]);

  useEffect(() => {
    if (filterError) {
      enqueueSnackbar(filterError, { variant: 'error' });
    }
  }, [filterError, enqueueSnackbar]);
  const buildMongoFilter = useCallback((conditionsToUse = conditions) => {
    // Helper function to convert value based on field type
    const convertValue = (value, fieldType) => {
      if (fieldType === 'number') {
        const numValue = parseFloat(value);
        if (Number.isNaN(numValue)) {
          throw new Error(`Invalid number value: ${value}`);
        }
        return numValue;
      }
      if (fieldType === 'date' || fieldType === 'datetime') {
        const dateValue = new Date(value);
        if (Number.isNaN(dateValue.getTime())) {
          throw new Error(`Invalid date value: ${value}`);
        }
        return dateValue;
      } return value;
    };    // Helper function to escape special regex characters
    const escapeRegex = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');    // Process conditions and group them properly based on logical operators
    const processedConditions = [];

    conditionsToUse.forEach((condition, index) => {
      if (!condition.field || condition.value === '') return;

      const conditionObj = {};
      const fieldSchema = getFieldSchema(condition.field);

      try {
        switch (condition.operator) {
          case 'eq':
            if (fieldSchema.type === 'string') {
              // Case-insensitive exact match for string fields
              conditionObj[condition.field] = { $regex: `^${escapeRegex(condition.value)}$`, $options: 'i' };
            } else {
              conditionObj[condition.field] = convertValue(condition.value, fieldSchema.type);
            }
            break;
          case 'ne':
            if (fieldSchema.type === 'string') {
              // Case-insensitive not equal for string fields
              conditionObj[condition.field] = { $not: { $regex: `^${escapeRegex(condition.value)}$`, $options: 'i' } };
            } else {
              conditionObj[condition.field] = { $ne: convertValue(condition.value, fieldSchema.type) };
            }
            break;
          case 'gt':
            conditionObj[condition.field] = { $gt: convertValue(condition.value, fieldSchema.type) };
            break;
          case 'gte':
            conditionObj[condition.field] = { $gte: convertValue(condition.value, fieldSchema.type) };
            break;
          case 'lt':
            conditionObj[condition.field] = { $lt: convertValue(condition.value, fieldSchema.type) };
            break;
          case 'lte':
            conditionObj[condition.field] = { $lte: convertValue(condition.value, fieldSchema.type) };
            break;
          case 'contains':
            conditionObj[condition.field] = { $regex: escapeRegex(condition.value), $options: 'i' };
            break;
          case 'in': {
            let values;
            if (Array.isArray(condition.value)) {
              // For user-select fields, value is already an array
              values = condition.value;
            } else {
              // For string fields, split by comma
              values = condition.value.split(',').map((v) => {
                const trimmedValue = v.trim();
                return fieldSchema.type === 'number' ? convertValue(trimmedValue, fieldSchema.type) : trimmedValue;
              });
            }

            if (fieldSchema.type === 'string') {
              // Case-insensitive matching for string fields using $in with regex
              conditionObj[condition.field] = {
                $in: values.map(value => new RegExp(`^${escapeRegex(value)}$`, 'i'))
              };
            } else {
              conditionObj[condition.field] = { $in: values };
            }
            break;
          }
          case 'nin': {
            let values;
            if (Array.isArray(condition.value)) {
              // For user-select fields, value is already an array
              values = condition.value;
            } else {
              // For string fields, split by comma
              values = condition.value.split(',').map((v) => {
                const trimmedValue = v.trim();
                return fieldSchema.type === 'number' ? convertValue(trimmedValue, fieldSchema.type) : trimmedValue;
              });
            }

            if (fieldSchema.type === 'string') {
              // Case-insensitive not in for string fields using $nin with regex
              conditionObj[condition.field] = {
                $nin: values.map(value => new RegExp(`^${escapeRegex(value)}$`, 'i'))
              };
            } else {
              conditionObj[condition.field] = { $nin: values };
            }
            break;
          }
          case 'range': {
            if (condition.value && condition.valueEnd) {
              const startValue = convertValue(condition.value, fieldSchema.type);
              const endValue = convertValue(condition.valueEnd, fieldSchema.type);
              conditionObj[condition.field] = { $gte: startValue, $lte: endValue };
            }
            break;
          }
          case 'exists':
            conditionObj[condition.field] = { $exists: condition.value === 'true' };
            break;
          default:
            conditionObj[condition.field] = convertValue(condition.value, fieldSchema.type);
        }
      } catch (error) {
        console.warn(`Skipping invalid condition for field ${condition.field}:`, error.message);
        return;
      }

      processedConditions.push({
        condition: conditionObj,
        logicalOperator: condition.logicalOperator,
        index
      });
    });

    if (processedConditions.length === 0) {
      return {};
    }

    if (processedConditions.length === 1) {
      return processedConditions[0].condition;
    }

    // Group conditions by consecutive logical operators
    const groups = [];
    let currentGroup = [processedConditions[0].condition];
    let currentOperator = null;

    // Start from the second condition
    for (let i = 1; i < processedConditions.length; i += 1) {
      const currentCondition = processedConditions[i];

      if (currentOperator === null) {
        // This is the first logical operator we encounter
        currentOperator = currentCondition.logicalOperator;

        if (currentOperator === 'OR') {
          // The first condition should also be part of the OR group
          currentGroup.push(currentCondition.condition);
        } else {
          // AND: finalize current group and start new one
          groups.push({
            operator: 'AND', // Default for first group when next is AND
            conditions: [...currentGroup]
          });
          currentGroup = [currentCondition.condition];
        }
      } else if (currentCondition.logicalOperator === currentOperator) {
        // Same operator as current group
        currentGroup.push(currentCondition.condition);
      } else {
        // Different operator: finalize current group and start new one
        groups.push({
          operator: currentOperator,
          conditions: [...currentGroup]
        });
        currentGroup = [currentCondition.condition];
        currentOperator = currentCondition.logicalOperator;
      }
    }

    // Add the final group
    if (currentGroup.length > 0) {
      groups.push({
        operator: currentOperator || 'AND',
        conditions: currentGroup
      });
    }

    // Build the final filter
    if (groups.length === 1) {
      const group = groups[0];
      if (group.conditions.length === 1) {
        return group.conditions[0];
      }
      if (group.operator === 'OR') {
        return { $or: group.conditions };
      }
      return { $and: group.conditions };
    }

    // Multiple groups: combine with $and
    const groupQueries = groups.map(group => {
      if (group.conditions.length === 1) {
        return group.conditions[0];
      }
      if (group.operator === 'OR') {
        return { $or: group.conditions };
      }
      return { $and: group.conditions };
    });

    return { $and: groupQueries };
  }, [conditions]);

  const addCondition = () => {
    setConditions([
      ...conditions,
      {
        id: Date.now(),
        field: '',
        operator: '',
        value: '',
        valueEnd: '', // For range operations
        logicalOperator: 'AND',
      },
    ]);
  };

  const removeCondition = (id) => {
    setConditions(conditions.filter((c) => c.id !== id));
  }; const updateCondition = (id, field, value) => {
    setConditions(prevConditions =>
      prevConditions.map((c) => (c.id === id ? { ...c, [field]: value } : c))
    );
  };

  const updateConditionBatch = (id, updates) => {
    setConditions(prevConditions =>
      prevConditions.map((c) => (c.id === id ? { ...c, ...updates } : c))
    );
  }; const handleApplyFilter = () => {
    const mongoFilter = buildMongoFilter();
    const filterName = currentFilterName || 'Custom Filter';

    const newAppliedFilter = {
      id: Date.now(),
      name: filterName,
      conditions: [...conditions],
      mongoFilter,
      type: filterType,
    };

    // Replace any existing filter with the new one (single filter mode)
    const updatedAppliedFilters = [newAppliedFilter];

    dispatch(applyCustomFilters(updatedAppliedFilters));
    onApplyFilters(updatedAppliedFilters);
    enqueueSnackbar('Filter applied successfully!', { variant: 'success' });
  };
  const handleApplySavedFilter = (filter) => {
    const appliedFilter = {
      id: Date.now(),
      name: filter.name,
      conditions: [], // We could parse the MongoDB filter back to conditions if needed
      mongoFilter: filter.filterObject,
      type: filterType,
      savedFilterId: filter._id,
    };

    // Replace any existing filter with the new one (single filter mode)
    const updatedAppliedFilters = [appliedFilter];

    dispatch(applyCustomFilters(updatedAppliedFilters));
    onApplyFilters(updatedAppliedFilters);
    setFilterMenuAnchor(null);
    enqueueSnackbar(`Filter "${filter.name}" applied!`, { variant: 'success' });
  };

  const handleSaveFilter = async () => {
    if (!currentFilterName.trim()) {
      enqueueSnackbar('Please enter a filter name', { variant: 'error' });
      return;
    }

    const filterData = {
      name: currentFilterName,
      description: currentFilterDescription,
      type: filterType,
      filterObject: buildMongoFilter(),
    };

    let result;
    if (editingFilterId) {
      result = await dispatch(updateCustomFilter(editingFilterId, filterData));
    } else {
      result = await dispatch(createCustomFilter(filterData));
    }

    if (result.success) {
      enqueueSnackbar(
        `Filter ${editingFilterId ? 'updated' : 'saved'} successfully!`,
        { variant: 'success' }
      );
      setSaveDialogOpen(false);
      setCurrentFilterName('');
      setCurrentFilterDescription('');
      setEditingFilterId(null);
    } else {
      enqueueSnackbar(result.error, { variant: 'error' });
    }
  };
  // Helper function to format values when loading saved filters
  const formatValueForField = (fieldName, value) => {
    const fieldSchema = getFieldSchema(fieldName);

    if (fieldSchema.type === 'date') {
      // Convert date to YYYY-MM-DD format for input[type="date"]
      if (value instanceof Date) {
        return value.toISOString().split('T')[0];
      }
      if (typeof value === 'string') {
        const date = new Date(value);
        if (!Number.isNaN(date.getTime())) {
          return date.toISOString().split('T')[0];
        }
      }
    } else if (fieldSchema.type === 'datetime') {
      // Convert datetime to YYYY-MM-DDTHH:MM format for input[type="datetime-local"]
      if (value instanceof Date) {
        const isoString = value.toISOString();
        return isoString.slice(0, 16); // Remove seconds and milliseconds
      }
      if (typeof value === 'string') {
        const date = new Date(value);
        if (!Number.isNaN(date.getTime())) {
          const isoString = date.toISOString();
          return isoString.slice(0, 16); // Remove seconds and milliseconds
        }
      }
    } else if (fieldSchema.type === 'user-select' && Array.isArray(value)) {
      // For user select fields with multiple values, keep as array for proper handling
      return value;
    } else if (fieldSchema.type === 'select' && Array.isArray(value)) {
      // For select fields with multiple values, keep as array for proper handling
      return value;
    } else if ((fieldSchema.type === 'string') && Array.isArray(value)) {
      // For string fields with multiple values, join them
      return value.join(', ');
    }

    return value;
  };

  const loadSavedFilter = (filter) => {
    // Convert MongoDB filter back to UI conditions (simplified version)
    const newConditions = [];
    const filterObj = filter.filterObject || {};

    const parseConditions = (obj, logicalOp = 'AND') => {
      Object.entries(obj).forEach(([key, value], index) => {
        if (key === '$and') {
          value.forEach((subCondition) => parseConditions(subCondition, 'AND'));
        } else if (key === '$or') {
          value.forEach((subCondition) => parseConditions(subCondition, 'OR'));
        } else if (!key.startsWith('$')) {
          let operator = 'eq';
          let conditionValue = value;

          if (typeof value === 'object' && value !== null) {
            if (value.$gt !== undefined) {
              operator = 'gt';
              conditionValue = value.$gt;
            } else if (value.$gte !== undefined) {
              operator = 'gte';
              conditionValue = value.$gte;
            } else if (value.$lt !== undefined) {
              operator = 'lt';
              conditionValue = value.$lt;
            } else if (value.$lte !== undefined) {
              operator = 'lte';
              conditionValue = value.$lte;
            } else if (value.$ne !== undefined) {
              operator = 'ne';
              conditionValue = value.$ne;
            } else if (value.$regex !== undefined) {
              operator = 'contains';
              conditionValue = value.$regex;
            } else if (value.$in !== undefined) {
              operator = 'in';
              conditionValue = value.$in;
            } else if (value.$nin !== undefined) {
              operator = 'nin';
              conditionValue = value.$nin;
            } else if (value.$exists !== undefined) {
              operator = 'exists';
              conditionValue = value.$exists.toString();
            }
          } newConditions.push({
            id: Date.now() + index + Math.random(),
            field: key,
            operator,
            value: formatValueForField(key, conditionValue),
            logicalOperator: logicalOp,
          });
        }
      });
    };

    parseConditions(filterObj);
    setConditions(newConditions);
    setCurrentFilterName(filter.name);
    setCurrentFilterDescription(filter.description || '');
    setEditingFilterId(filter._id);
  };

  const handleDeleteFilter = async (filterId) => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Are you sure you want to delete this filter?')) {
      const result = await dispatch(deleteCustomFilter(filterId));
      if (result.success) {
        enqueueSnackbar('Filter deleted successfully!', { variant: 'success' });
      } else {
        enqueueSnackbar(result.error, { variant: 'error' });
      }
    }
  }; const removeAppliedFilter = () => {
    // In single filter mode, removing a filter means clearing all filters
    const updatedFilters = [];
    dispatch(applyCustomFilters(updatedFilters));
    onApplyFilters(updatedFilters);
  };

  const clearAllConditions = () => {
    setConditions([]);
    setCurrentFilterName('');
    setCurrentFilterDescription('');
    setEditingFilterId(null);
  }; const clearAllAppliedFilters = () => {
    // Clear both applied filters and current conditions
    dispatch(applyCustomFilters([]));
    onApplyFilters([]);
    clearAllConditions();
  };

  // Component render starts here
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Box 
          display="flex" 
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between" 
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          gap={{ xs: 1, sm: 0 }}
          mb={2}
        >
          <Box mb={{ xs: 1, sm: 0 }}>
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Custom Filter Builder
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
              Only one filter can be applied at a time. Use multiple conditions within a single filter for complex queries.
            </Typography>
          </Box>
          <Stack 
            direction="row" 
            spacing={{ xs: 0.5, sm: 1 }}
            flexWrap="wrap"
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <Button
              startIcon={<FilterIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
              onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
              variant="outlined"
              size="small"
              sx={{ 
                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                px: { xs: 1, sm: 1.5 },
                flex: { xs: '1 1 auto', sm: '0 0 auto' },
                minWidth: 0,
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Saved Filters </Box>({actualCustomFilters.length})
            </Button>
            <Button 
              startIcon={<AddIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />} 
              onClick={addCondition} 
              variant="contained" 
              size="small"
              sx={{ 
                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                px: { xs: 1, sm: 1.5 },
                flex: { xs: '1 1 auto', sm: '0 0 auto' },
                minWidth: 0,
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Add </Box>Condition
            </Button>
          </Stack>
        </Box>{/* Applied Filters */}
        <AppliedFiltersDisplay
          appliedFilters={appliedFilters}
          onRemoveFilter={() => removeAppliedFilter()}
          onClearAll={clearAllAppliedFilters}
        />        {/* Filter Conditions */}
        <Box mb={3}>
          {conditions.length > 0 && (
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle2">Filter Conditions:</Typography>
              <Button size="small" variant="text" onClick={clearAllConditions}>
                Clear Conditions
              </Button>
            </Box>
          )}
          {conditions.map((condition, index) => (
            <Card key={condition.id} variant="outlined" sx={{ mb: 2, p: 2 }}>              <FilterConditionRow
              condition={condition}
              index={index}
              updateCondition={updateCondition}
              updateConditionBatch={updateConditionBatch}
              removeCondition={removeCondition}
              availableFields={availableFields}
            />
            </Card>
          ))}

          {conditions.length === 0 && (
            <Box textAlign="center" py={4}>
              <Typography variant="body2" color="text.secondary">
                No conditions added. Click "Add Condition" to start building your filter.
              </Typography>
            </Box>
          )}
        </Box>        {/* Actions */}
        <Box 
          display="flex" 
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between" 
          alignItems={{ xs: 'stretch', sm: 'center' }}
          gap={{ xs: 1, sm: 0 }}
        >
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={{ xs: 1, sm: 2 }}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <Button
              variant="contained"
              onClick={handleApplyFilter}
              disabled={conditions.length === 0 || isFilterLoading}
              startIcon={isFilterLoading ? <CircularProgress size={16} /> : <FilterIcon />}
              fullWidth={false}
              size="small"
              sx={{ 
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                px: { xs: 1.5, sm: 2 },
              }}
            >
              Apply Filter
            </Button>
            <Button
              variant="outlined"
              onClick={() => setSaveDialogOpen(true)}
              disabled={conditions.length === 0 || isFilterLoading}
              startIcon={<SaveIcon />}
              fullWidth={false}
              size="small"
              sx={{ 
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                px: { xs: 1.5, sm: 2 },
              }}
            >
              Save Filter
            </Button>
          </Stack>
        </Box>

        {/* Filter Preview */}
        {/* {Object.keys(previewFilter).length > 0 && (
          <Box mt={3}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              MongoDB Filter Preview:
            </Typography>
            <Box
              component="pre"
              sx={{
                backgroundColor: 'grey.100',
                p: 2,
                borderRadius: 1,
                fontSize: '0.75rem',
                overflow: 'auto',
                maxHeight: 200,
              }}
            >
              {JSON.stringify(previewFilter, null, 2)}            </Box>
          </Box>
        )} */}

        {/* Saved Filters Menu */}        <SavedFiltersMenu
          anchorEl={filterMenuAnchor}
          open={Boolean(filterMenuAnchor)}
          onClose={() => setFilterMenuAnchor(null)}
          customFilters={actualCustomFilters}
          isFilterLoading={isFilterLoading}
          onLoadFilter={(filter) => {
            loadSavedFilter(filter);
            setFilterMenuAnchor(null);
          }}
          onApplyFilter={handleApplySavedFilter}
          onDeleteFilter={handleDeleteFilter}
        />

        {/* Save Filter Dialog */}        <SaveFilterDialog
          open={saveDialogOpen}
          onClose={() => setSaveDialogOpen(false)}
          editingFilterId={editingFilterId}
          currentFilterName={currentFilterName}
          setCurrentFilterName={setCurrentFilterName}
          currentFilterDescription={currentFilterDescription}
          setCurrentFilterDescription={setCurrentFilterDescription}
          isFilterLoading={isFilterLoading}
          onSaveFilter={handleSaveFilter}
        />
      </CardContent>
    </Card>
  );
};

LineTradeFilterBuilder.propTypes = {
  filterType: PropTypes.oneOf(Object.keys(FILTER_TYPES)),
  onApplyFilters: PropTypes.func.isRequired,
  appliedFilters: PropTypes.array,
};

export default LineTradeFilterBuilder;
